import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Trophy, Star, MapPin, Crown, History } from "lucide-react";

/**
 * WinnersWall — homepage "This Month's Champions" section.
 * Three cards, ordered exactly per the community's request:
 *   1. Lifetime Legend  — highest all-time run+walk KM.
 *   2. Monthly Champion — highest run+walk KM in the current IST month.
 *   3. Top Cities       — top-3 cities/districts by monthly KM, with #1 runner.
 * Followed by a "Past monthly winners" strip. Returns null when there's nothing
 * to show yet.
 */
export default function WinnersWall() {
  const [w, setW] = useState({
    month: "",
    month_label: "",
    lifetime_legend: null,
    monthly_champion: null,
    top_cities: [],
    past: [],
  });

  useEffect(() => {
    api.get("/winners").then((r) => setW(r.data)).catch(() => {});
  }, []);

  const cities = w.top_cities || [];
  const anyChampion = w.lifetime_legend || w.monthly_champion || cities.length > 0;
  const anyPast = (w.past || []).length > 0;
  if (!anyChampion && !anyPast) return null;

  return (
    <section
      data-testid="winners-wall"
      className="relative border-t border-zinc-800 bg-zinc-950 overflow-hidden"
    >
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-orange-600/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="text-center mb-12">
          <p className="font-mono text-[11px] tracking-[0.35em] text-orange-500 uppercase inline-flex items-center gap-2">
            <Trophy size={12} /> CHAMPIONS OF THE PACK <Trophy size={12} />
          </p>
          <h2
            data-testid="winners-wall-title"
            className="font-display text-4xl sm:text-5xl text-white mt-3 leading-[0.95]"
          >
            This month&apos;s <span className="text-orange-500">champions.</span>
          </h2>
          {w.month_label && (
            <p
              data-testid="winners-month-label"
              className="font-mono text-[11px] tracking-[0.3em] text-orange-400 uppercase mt-3"
            >
              {w.month_label} · MONTHLY BOARD RESETS ON THE 1ST
            </p>
          )}
          <p className="text-zinc-400 mt-3 max-w-xl mx-auto text-sm">
            Recognising the all-time legend, the runner leading this month, and the top cities powering our pack.
          </p>
        </div>

        {anyChampion && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* 1) LIFETIME LEGEND */}
            <RunnerCard
              kind="lifetime"
              tone="legend"
              icon={Crown}
              label="LIFETIME LEGEND"
              subtitle="Highest all-time run + walk KM"
              badge="ALL-TIME #1"
              data={w.lifetime_legend}
              primary={w.lifetime_legend ? `${w.lifetime_legend.total_km}` : null}
              primaryUnit="KM"
              secondary={
                w.lifetime_legend
                  ? `${w.lifetime_legend.badges || 0} badge${(w.lifetime_legend.badges || 0) === 1 ? "" : "s"} earned`
                  : null
              }
            />

            {/* 2) MONTHLY CHAMPION */}
            <RunnerCard
              kind="monthly"
              tone="winner"
              icon={Trophy}
              label="MONTHLY CHAMPION"
              subtitle={w.month_label ? `${w.month_label} — run + walk` : "This month — run + walk"}
              badge="THIS MONTH"
              data={w.monthly_champion}
              primary={w.monthly_champion ? `${w.monthly_champion.total_km}` : null}
              primaryUnit="KM"
              secondary={
                w.monthly_champion
                  ? `${w.monthly_champion.days} active day${w.monthly_champion.days === 1 ? "" : "s"} this month`
                  : null
              }
            />

            {/* 3) TOP 3 CITIES / DISTRICTS */}
            <CitiesCard
              icon={MapPin}
              label="TOP 3 CITIES · DISTRICTS"
              subtitle={w.month_label ? `${w.month_label} — most KM logged` : "This month — most KM logged"}
              cities={cities}
            />
          </div>
        )}

        {anyPast && (
          <div data-testid="winners-past" className="mt-16">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-2">
              <div>
                <p className="font-mono text-[11px] tracking-[0.3em] text-orange-500 uppercase inline-flex items-center gap-2">
                  <History size={12} /> HALL OF FAME
                </p>
                <h3 className="font-display text-2xl sm:text-3xl text-white mt-1">
                  Past monthly <span className="text-orange-500">champions.</span>
                </h3>
              </div>
              <Link
                to="/leaderboard"
                className="font-mono text-[11px] tracking-[0.3em] text-orange-500 hover:text-orange-400 uppercase"
              >
                FULL BOARD →
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {w.past.map((p) => (
                <PastWinnerCard key={p.month} entry={p} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function PastWinnerCard({ entry }) {
  const r = entry.top_runner;
  return (
    <Link
      to={`/profile/${r.user_id}`}
      data-testid={`past-winner-${entry.month}`}
      className="hairline bg-zinc-900 p-4 flex items-center gap-3 hover:border-orange-600/50 hover:-translate-y-0.5 transition"
    >
      <Avatar className="h-11 w-11 rounded-none shrink-0">
        <AvatarImage src={r.picture} />
        <AvatarFallback className="rounded-none bg-zinc-800 text-white font-display">
          {r.name?.[0]?.toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-orange-500">
          {entry.month_label}
        </p>
        <p className="font-display text-base text-white truncate leading-tight mt-0.5">
          {r.name}
        </p>
        {r.city && (
          <p className="font-mono text-[9px] tracking-widest text-zinc-500 uppercase truncate">
            {r.city}
          </p>
        )}
      </div>
      <div className="text-right">
        <p className="font-display text-xl text-orange-400 leading-none">{r.total_km}</p>
        <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-zinc-500 mt-1">
          KM · {r.days}d
        </p>
      </div>
    </Link>
  );
}

function RunnerCard({ kind, tone, icon: Icon, label, subtitle, badge, data, primary, primaryUnit, secondary }) {
  const isBone = tone === "legend";
  const toneCls = isBone
    ? "surface-bone hairline-bone"
    : "bg-zinc-900 border border-white/10 hover:border-orange-600/60";

  if (!data) {
    return (
      <div
        data-testid={`winner-${kind}-empty`}
        className={`${toneCls} p-6 opacity-60 min-h-[280px] flex flex-col`}
      >
        <div className="flex items-center gap-2">
          <Icon size={14} className={isBone ? "text-stone-500" : "text-zinc-500"} />
          <p className={`font-mono text-[10px] tracking-[0.3em] uppercase ${isBone ? "text-stone-500" : "text-zinc-500"}`}>{label}</p>
        </div>
        <p className={`font-mono text-[10px] tracking-widest uppercase mt-2 ${isBone ? "text-stone-500" : "text-zinc-600"}`}>{subtitle}</p>
        <div className="flex-1 flex items-center justify-center">
          <p className={`font-mono text-xs tracking-widest uppercase text-center ${isBone ? "text-stone-500" : "text-zinc-600"}`}>
            Awaiting a champion.<br />Could be you.
          </p>
        </div>
      </div>
    );
  }

  return (
    <Link
      to={`/profile/${data.user_id}`}
      data-testid={`winner-${kind}`}
      className={`group block card-editorial ${toneCls} p-6 min-h-[280px] flex flex-col`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon size={14} className={isBone ? "text-orange-600" : "text-orange-500"} />
          <p className={`font-mono text-[10px] tracking-[0.3em] uppercase ${isBone ? "text-orange-600" : "text-orange-500"}`}>{label}</p>
        </div>
        {badge && (
          <span className={`font-mono text-[8px] tracking-[0.2em] uppercase px-1.5 py-0.5 ${isBone ? "bg-stone-900 text-bone" : "bg-orange-600 text-white"}`}>
            {badge}
          </span>
        )}
      </div>
      <p className={`font-mono text-[10px] tracking-widest uppercase mt-1 truncate ${isBone ? "text-stone-500" : "text-zinc-500"}`}>
        {subtitle}
      </p>

      <div className="mt-5 flex items-center gap-3">
        <Avatar className="h-14 w-14 rounded-none avatar-editorial">
          <AvatarImage src={data.picture} />
          <AvatarFallback className={`rounded-none font-display text-2xl ${isBone ? "bg-stone-200 text-stone-900" : "bg-zinc-800 text-white"}`}>
            {data.name?.[0]?.toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className={`font-display text-2xl tracking-wide leading-tight transition truncate ${isBone ? "text-stone-900 group-hover:text-orange-600" : "text-white group-hover:text-orange-400"}`}>
            {data.name}
          </p>
          {data.city && (
            <p className={`font-mono text-[10px] tracking-widest uppercase truncate ${isBone ? "text-orange-600" : "text-orange-500"}`}>
              {data.city}
            </p>
          )}
        </div>
      </div>

      <div className="mt-auto pt-5">
        <div className="flex items-baseline gap-2">
          <span className={`font-display text-5xl leading-none ${isBone ? "text-stone-900" : "text-orange-500"}`}>{primary}</span>
          <span className={`font-mono text-[10px] tracking-[0.3em] uppercase ${isBone ? "text-stone-500" : "text-zinc-500"}`}>{primaryUnit}</span>
        </div>
        {secondary && (
          <p className={`font-mono text-[10px] tracking-widest uppercase mt-2 ${isBone ? "text-stone-600" : "text-zinc-400"}`}>{secondary}</p>
        )}
      </div>
    </Link>
  );
}

const CITY_MEDAL = [
  { rank: "1", ring: "border-orange-500",  text: "text-orange-400" },
  { rank: "2", ring: "border-zinc-300",    text: "text-zinc-200" },
  { rank: "3", ring: "border-amber-700",   text: "text-amber-500" },
];

function CitiesCard({ icon: Icon, label, subtitle, cities }) {
  const empty = !cities || cities.length === 0;
  return (
    <div
      data-testid={empty ? "winner-cities-empty" : "winner-cities"}
      className={`hairline bg-gradient-to-br from-orange-600/10 to-zinc-900 border-orange-600/40 p-6 min-h-[280px] flex flex-col ${empty ? "opacity-60" : ""}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon size={14} className="text-orange-500" />
          <p className="font-mono text-[10px] tracking-[0.3em] text-orange-500 uppercase">{label}</p>
        </div>
        <span className="font-mono text-[8px] tracking-[0.2em] uppercase px-1.5 py-0.5 bg-orange-600 text-white">
          THIS MONTH
        </span>
      </div>
      <p className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase mt-1">
        {subtitle}
      </p>

      {empty ? (
        <div className="flex-1 flex items-center justify-center">
          <p className="font-mono text-xs tracking-widest text-zinc-600 uppercase text-center">
            No city has logged<br />a run yet this month.
          </p>
        </div>
      ) : (
        <ol className="mt-5 space-y-3 flex-1">
          {cities.map((c, i) => {
            const meta = CITY_MEDAL[i] || CITY_MEDAL[2];
            const tr = c.top_runner;
            return (
              <li
                key={c.city}
                data-testid={`winner-city-${i + 1}`}
                className="flex items-center gap-3"
              >
                <div
                  className={`w-8 h-8 shrink-0 border-2 ${meta.ring} grid place-items-center font-display text-sm ${meta.text}`}
                >
                  {meta.rank}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display text-base text-white truncate leading-tight">
                    {c.city}
                  </p>
                  <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-zinc-500 truncate">
                    TOP · {tr?.name || "—"}
                    {c.runner_count > 1 && ` · +${c.runner_count - 1} more`}
                  </p>
                </div>
                <div className="text-right">
                  <p className={`font-display text-lg leading-none ${meta.text}`}>
                    {c.total_km}
                  </p>
                  <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-zinc-500 mt-0.5">
                    KM
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {!empty && (
        <Link
          to="/leaderboard"
          className="mt-4 font-mono text-[10px] tracking-[0.3em] text-orange-500 hover:text-orange-400 uppercase self-start"
        >
          FULL CITY BOARD →
        </Link>
      )}
    </div>
  );
}
